// New applications — the alert at the top of the nvRecruiter Desk panel
// (owner, 2026-09-29: "add this to a new recruiting table on desk with an
// alert of new application"; 2026-09-30: "there is already an nvRecruiter
// desk table so there doesn't need to also be an applications table" — so it
// lives inside RecruitingDeskPanel.tsx, not as a panel of its own). Every
// application the Indeed intake recorded that nobody has emailed yet — GET
// /api/v1/recruiting/new-applications (src/recruiting.js listNewApplications
// holds the rule). The clinic's manager is emailed about each one
// (src/recruiting_notify.js); this is where they find it.
//
// A row leaves once someone emails the applicant from their nvRecruiter page
// (owner, 2026-09-30: "once they email candidate it can be removed") — or
// they are hired, turned down, or marked do-not-contact. The Email button
// opens their page. Renders nothing while loading or when nobody is waiting,
// so the panel's own counts stay where they were.
import type { JSX } from 'preact';
import { useResource } from '../../lib/store';
import { LOCATION_NAMES, type LocationCode } from '../../lib/locations';
import {
  fetchNewApplications, formatCivilDate, POSITION_WORDS, RECRUITING_NEW_APPLICATIONS_KEY,
  type NewApplication, type NewApplicationsResponse,
} from '../../lib/recruitingApi';
import { navigate } from '../../lib/router';
import { FaultState } from '../FaultState';

/** The rows one panel instance shows. Pure. */
export function newApplicationsInScope(
  data: NewApplicationsResponse,
  scope: LocationCode | 'all',
  visible: LocationCode[],
): NewApplication[] {
  if (scope === 'all') {
    return data.applications.filter((a) => a.locationCode !== null && visible.includes(a.locationCode));
  }
  return data.applications.filter((a) => a.locationCode === scope);
}

function fullName(a: NewApplication): string {
  return [a.firstName, a.middleName, a.lastName].filter(Boolean).join(' ');
}

export function NewApplicationsSection(
  { scope, visible }: { scope: LocationCode | 'all'; visible: LocationCode[] },
): JSX.Element | null {
  const load = useResource<NewApplicationsResponse>(RECRUITING_NEW_APPLICATIONS_KEY, fetchNewApplications);

  if (load.status === 'loading') return null;
  if (load.status === 'fault') {
    return <FaultState name="recruiting_new_applications_load_failed" detail={load.error} />;
  }

  const rows = newApplicationsInScope(load.data, scope, visible);
  if (rows.length === 0) return null;

  return (
    <div class="new-applications" data-testid="new-applications">
      <p data-testid="new-applications-alert">
        <span class="badge warn">{rows.length} new</span>
        {' '}
        {rows.length === 1 ? 'applicant is' : 'applicants are'} waiting to hear from you
        {load.data.truncated ? ' (showing the newest 200)' : ''}
      </p>
      {/* Owner, 2026-10-12: rows read like My Mail (the nvRecruiter box is
         4 columns wide — a six-column table did not fit) — the applicant
         bold with the date applied small on the right; job, clinic and
         resume muted underneath, with the Email button. */}
      <ul class="desk-list" data-testid="new-applications-table">
        {rows.map((a) => (
          <li class="desk-row" key={a.id} data-testid={`new-application-${a.id}`}>
            <span class="desk-row-line">
              <a class="desk-row-main candidate-link" href={`/recruiting?candidate=${a.candidateId}`}>{fullName(a)}</a>
              <span class="desk-row-value">{formatCivilDate(a.appliedAt)}</span>
            </span>
            <span class="desk-row-line">
              <span class="desk-row-sub">
                {(a.position && POSITION_WORDS[a.position]) || a.position || '—'}
                {scope === 'all' && visible.length > 1 ? ` · ${a.locationCode ? LOCATION_NAMES[a.locationCode] : '—'}` : ''}
                {' · Resume: '}
                {a.resumeUrl
                  ? <a href={a.resumeUrl} target="_blank" rel="noopener noreferrer">Open</a>
                  : <span class="muted">None</span>}
              </span>
              <button
                type="button"
                class="btn desk-row-action"
                data-testid={`new-application-email-${a.id}`}
                onClick={() => navigate(`/recruiting?candidate=${a.candidateId}`)}
              >
                Email
              </button>
            </span>
          </li>
        ))}
      </ul>
    </div>
  );
}
